// Table pager: "Page X of Y · N total", Prev / Next and a page-size select.
// Same look as the Deposits history pager (ui-pager bar, ui-btn buttons, Space
// Mono label). Works for server-paged lists (pass total from the response) and,
// via useClientPage, for small lists paged in the browser.
import { useEffect, useMemo, useState } from "react";

export const PAGE_SIZES = [10, 25, 50];

export function TablePager({ page, total, size, onPage, onSize, sizes = PAGE_SIZES, testId = "pager", always = false, boxed = false }) {
  const pages = Math.max(1, Math.ceil((total || 0) / (size || 1)));
  // Nothing to page and no smaller size to switch to: keep the table clean.
  if (!always && (total || 0) <= Math.min(...sizes)) return null;
  return (
    <div className={`ui-pager sc-tpager ${boxed ? "ui-card is-boxed" : ""}`} data-testid={testId}>
      <span className="ui-mono ui-muted sc-tpager-label">
        Page {Math.min(page, pages)} of {pages} · {(total || 0).toLocaleString("en-IN")} total
      </span>
      <div className="sc-tpager-ctrls">
        {onSize && (
          <select data-testid={`${testId}-size`} className="ui-select sc-tpager-size" value={size} aria-label="Rows per page"
            onChange={(e) => { onSize(Number(e.target.value)); onPage(1); }}>
            {sizes.map((s) => <option key={s} value={s}>{s} / page</option>)}
          </select>
        )}
        <button data-testid={`${testId}-prev`} type="button" className="ui-btn ui-btn-sm sc-tpager-btn"
          onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1}>← Prev</button>
        <button data-testid={`${testId}-next`} type="button" className="ui-btn ui-btn-sm sc-tpager-btn"
          onClick={() => onPage(Math.min(pages, page + 1))} disabled={page >= pages}>Next →</button>
      </div>
      <style>{`
        .sc-tpager-label { font-size: 12px; font-weight: 700; }
        .sc-tpager.is-boxed { border-top: none; }
        .sc-tpager-ctrls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .sc-tpager-btn { min-height: 36px; }
        .sc-tpager-size { min-height: 36px; width: auto; }
        @media (max-width: 480px) {
          .sc-tpager { flex-direction: column; align-items: stretch; }
          .sc-tpager-ctrls { justify-content: space-between; }
          .sc-tpager-btn { flex: 1; }
        }
      `}</style>
    </div>
  );
}

/**
 * Browser-side paging for small lists. Returns the visible slice plus the props
 * TablePager needs. `resetKey` (e.g. the active filter/search) jumps back to page 1.
 */
export function useClientPage(items, initialSize = 10, resetKey = "") {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(initialSize);
  const total = items ? items.length : 0;
  const pages = Math.max(1, Math.ceil(total / size));
  useEffect(() => { setPage(1); }, [resetKey]);
  // Deleting the last row of the last page must not strand the user on an empty page.
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const cur = Math.min(page, pages);
  const slice = useMemo(() => (items || []).slice((cur - 1) * size, cur * size), [items, cur, size]);
  return { slice, pager: { page: cur, total, size, onPage: setPage, onSize: setSize } };
}
